import { useEffect, useState } from "react";
import { http } from "../api";
import { Trash2, RotateCcw, X, Filter, GitMerge, Undo2, CheckCircle2 } from "lucide-react";
import { PageHeader, EmptyState } from "../shared";

const TYPE_LABELS = {
  contact: "People",
  organization: "Organizations",
  note_template: "Templates",
  form: "Custom forms",
  scheme: "Govt schemes",
};

export default function RecycleBin({ user }) {
  const isSuper = user?.role === "Super Admin";
  const [data, setData] = useState({ items: [], counts: {}, total: 0 });
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [merges, setMerges] = useState([]);
  const [now, setNow] = useState(Date.now());

  const load = async () => {
    setLoading(true); setError("");
    try {
      const r = await http.get(`/recycle-bin${type ? `?entity_type=${type}` : ""}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load recycle bin");
    } finally { setLoading(false); }
  };

  const loadMerges = async () => {
    try {
      const r = await http.get("/merges/recent");
      setMerges(r.data.merges || []);
    } catch { /* noop */ }
  };

  useEffect(() => { if (isSuper) load(); }, [type, isSuper]);
  useEffect(() => { if (isSuper) loadMerges(); }, [isSuper]);
  useEffect(() => {
    if (!isSuper) return;
    const iv = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(iv);
  }, [isSuper]);

  if (!isSuper) {
    return (
      <>
        <PageHeader eyebrow="RECYCLE BIN" title="Recycle bin" subtitle="Super Admin only" />
        <EmptyState icon={Trash2} title="Super Admin access only" hint="Ask a Super Admin to review or restore deleted items." />
      </>
    );
  }

  const restore = async (row) => {
    try {
      await http.post(`/recycle-bin/${row.trash_id}/restore`);
      load();
    } catch (e) { window.alert(e.response?.data?.detail || "Restore failed"); }
  };
  const purge = async (row) => {
    if (!window.confirm("Permanently delete this item? This cannot be undone.")) return;
    await http.delete(`/recycle-bin/${row.trash_id}`);
    load();
  };

  const previewLabel = (row) => {
    const d = row.data || {};
    return d.name || d.title || d.text || d.family_head || d.form_id || row.entity_id;
  };

  const undoMerge = async (m) => {
    if (!window.confirm(`Undo the merge that combined "${m.loser_name}" into "${m.winner_name}"? The loser profile will be restored.`)) return;
    try {
      await http.post(`/merges/${m.merge_id}/undo`);
      await Promise.all([loadMerges(), load()]);
    } catch (e) { window.alert(e.response?.data?.detail || "Undo failed"); }
  };

  const remainingLabel = (mergedAtIso) => {
    const merged = new Date(mergedAtIso).getTime();
    const expires = merged + 24 * 3600 * 1000;
    const ms = expires - now;
    if (ms <= 0) return "expired";
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
  };

  return (
    <>
      <PageHeader eyebrow="GOVERNANCE" title="Recycle bin"
        subtitle="Restore deleted people, orgs, templates, forms and schemes — or purge them for good."
        actions={<button className="outline" data-testid="rb-refresh" onClick={() => { load(); loadMerges(); }}><RotateCcw size={14} /> Refresh</button>} />

      <section className="panel" data-testid="rb-merges-section">
        <div className="rb-merges-head">
          <b><GitMerge size={14} /> Recent merges (24h undo window)</b>
          <em>{merges.length}</em>
        </div>
        {merges.length === 0 ? (
          <p className="hint" style={{ padding: 20, textAlign: "center" }}>No merges recorded in the last 24 hours.</p>
        ) : (
          <div className="rb-merges-list" data-testid="rb-merges-list">
            {merges.map((m) => (
              <div key={m.merge_id} className={`rb-merge-row ${m.undone ? "rb-merge-undone" : ""}`} data-testid={`rb-merge-${m.merge_id}`}>
                <div className="rb-merge-body">
                  <div className="rb-merge-title">
                    <b>{m.loser_name || m.loser_id}</b>
                    <span className="rb-merge-arrow">→</span>
                    <b>{m.winner_name || m.winner_id}</b>
                    {m.undone && <span className="rb-merge-badge rb-merge-badge-undone" data-testid={`rb-merge-badge-${m.merge_id}`}><CheckCircle2 size={11} /> Undone</span>}
                  </div>
                  <small>
                    Mobile {m.mobile || "—"} · merged by {m.merged_by_name || "—"} · {new Date(m.merged_at).toLocaleString()}
                    {(m.overwritten_fields || []).length > 0 && <> · overwrote {(m.overwritten_fields || []).length} field(s)</>}
                  </small>
                </div>
                <div className="rb-merge-actions">
                  {m.undone ? (
                    <small className="rb-merge-undone-meta">
                      by {m.undone_by_name || "—"}<br />{m.undone_at ? new Date(m.undone_at).toLocaleString() : ""}
                    </small>
                  ) : m.undoable ? (
                    <>
                      <span className="rb-merge-countdown" data-testid={`rb-merge-countdown-${m.merge_id}`}>{remainingLabel(m.merged_at)}</span>
                      <button className="primary" data-testid={`rb-merge-undo-${m.merge_id}`} onClick={() => undoMerge(m)}><Undo2 size={13} /> Undo</button>
                    </>
                  ) : (
                    <span className="rb-merge-countdown rb-merge-expired">Expired</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="panel">
        <div className="rb-toolbar" data-testid="rb-toolbar">
          <div className="rb-chips">
            <button className={`rb-chip ${!type ? "on" : ""}`} data-testid="rb-chip-all" onClick={() => setType("")}>
              All <em>{data.total || 0}</em>
            </button>
            {Object.entries(TYPE_LABELS).map(([k, v]) => (
              <button key={k} className={`rb-chip ${type === k ? "on" : ""}`} data-testid={`rb-chip-${k}`} onClick={() => setType(k)}>
                {v} <em>{data.counts?.[k] || 0}</em>
              </button>
            ))}
          </div>
          <span className="hint"><Filter size={12} /> Only Super Admin can restore or purge.</span>
        </div>

        {error && <p className="hint" style={{ color: "#b91c1c" }}>{error}</p>}
        {loading ? (
          <p className="hint" style={{ padding: 30, textAlign: "center" }}>Loading…</p>
        ) : (data.items || []).length === 0 ? (
          <EmptyState icon={Trash2} title="Recycle bin is empty" hint="Deleted items land here for safekeeping." />
        ) : (
          <div className="rb-list" data-testid="rb-list">
            {(data.items || []).map((row) => (
              <div key={row.trash_id} className="rb-row" data-testid={`rb-row-${row.trash_id}`}>
                <div className="rb-body">
                  <div>
                    <span className="rb-type-chip">{TYPE_LABELS[row.entity_type] || row.entity_type}</span>
                    <b>{previewLabel(row)}</b>
                  </div>
                  <small>Deleted by {row.deleted_by_name || "—"} · {new Date(row.deleted_at).toLocaleString()}</small>
                </div>
                <div className="rb-actions">
                  <button className="primary" data-testid={`rb-restore-${row.trash_id}`} onClick={() => restore(row)}><RotateCcw size={13} /> Restore</button>
                  <button className="danger" data-testid={`rb-purge-${row.trash_id}`} onClick={() => purge(row)}><X size={13} /> Purge</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
